import { useState, type FormEvent, type ReactElement } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { ApiError, api } from '../lib/api';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { AuthLayout } from './AuthLayout';

export default function Login(): ReactElement {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation() as { state?: { from?: string } };
  const from = location.state?.from || '/';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Saber si el signup está abierto. Si está cerrado (beta privada), no
  // mostramos el link de "Crear cuenta" — solo invita confusión.
  const { data: authConfig } = useQuery({
    queryKey: ['auth-config'],
    queryFn: () => api<{ signupEnabled: boolean }>('/auth/config'),
    staleTime: 5 * 60 * 1000,
  });

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login({ email, password });
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError('Error inesperado. Probá de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      title="Iniciar sesión"
      subtitle="Volvé a tu diario · seguí registrando tus operaciones"
    >
      <form onSubmit={submit} className="space-y-4">
        <Input
          label="Email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoFocus
          placeholder="vos@ejemplo.com"
        />
        <Input
          label="Contraseña"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          placeholder="••••••••"
        />

        {error && (
          <div className="p-3 rounded-lg border border-loss/30 bg-loss/10 text-sm text-loss">
            {error}
          </div>
        )}

        <Button type="submit" loading={submitting} className="w-full" size="lg">
          {submitting ? 'Entrando…' : 'Entrar'}
        </Button>
      </form>

      <p className="mt-6 text-sm text-ink-subtle text-center">
        {authConfig?.signupEnabled ? (
          <>
            ¿Primera vez?{' '}
            <Link to="/signup" className="text-brand-500 hover:text-brand-400 font-medium">
              Crear cuenta
            </Link>
          </>
        ) : (
          <span className="text-ink-faint">
            Beta privada · acceso por invitación
          </span>
        )}
      </p>
    </AuthLayout>
  );
}
